import type {
  AuthSession,
  OAuthCallbackResult,
} from '@/platform/auth/AuthAdapter';
import { analytics, type LoginMethod } from '@/platform/analytics';
import type { AppThunk } from '@/platform/store/types';

import {
  logout,
  setLoading,
  setMfaChallenge,
  setMfaError,
  setToken,
  setUser,
} from './authSlice';

export interface LoginMeta {
  method: LoginMethod;
  via_mfa: boolean;
}

// Which flow issued the pending MFA challenge, so the OTP success can be
// attributed to password or Google.
let pendingMfaMethod: LoginMethod = 'password';

/** Stores a fresh session, runs the app's post-login hook and records the outcome. */
export const thunkSetSession =
  (session: AuthSession, meta?: LoginMeta): AppThunk =>
  (dispatch, _getState, { auth, onAuthenticated }) => {
    // The backend payload is not validated: a response without a token is a
    // failed login, never a half-open session.
    if (!session.token) {
      dispatch(setUser({ user: undefined }));
      dispatch(setToken({ token: null }));
      if (meta) {
        analytics.capture('auth:login_failed', { method: meta.method, reason: 'rejected' });
      }
      return;
    }
    dispatch(setUser({ user: session.user }));
    dispatch(setToken({ token: session.token }));
    auth.tokens.set(session.token);
    // Captured before AnalyticsSession identifies the user; PostHog merges the
    // anonymous event into the person on `$identify`.
    if (meta) analytics.capture('auth:login_succeeded', meta);
    onAuthenticated?.({ dispatch, user: session.user });
  };

export const thunkDoLogin =
  ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }): AppThunk<Promise<void>> =>
  async (dispatch, _getState, { auth }) => {
    dispatch(setLoading({ isLoading: true }));
    dispatch(setMfaChallenge(null));
    analytics.capture('auth:login_submitted', { method: 'password' });
    try {
      const result = await auth.login({ email: username, password });
      if (result.kind === 'mfa') {
        // Park the challenge in the slice; the LoginPage swaps to the OTP form.
        pendingMfaMethod = 'password';
        analytics.capture('auth:mfa_started', { method: 'password' });
        dispatch(setMfaChallenge(result.challenge));
        return;
      }
      dispatch(
        thunkSetSession(result.session, { method: 'password', via_mfa: false }),
      );
    } catch {
      analytics.capture('auth:login_failed', { method: 'password', reason: 'rejected' });
      dispatch(setUser({ user: undefined }));
    } finally {
      dispatch(setLoading({ isLoading: false }));
    }
  };

export const thunkVerifyMfa =
  (otp_code: string): AppThunk<Promise<void>> =>
  async (dispatch, getState, { auth }) => {
    const challenge = getState().auth.mfaChallenge;
    if (!challenge) {
      dispatch(setMfaError('No MFA challenge in progress.'));
      return;
    }
    dispatch(setLoading({ isLoading: true }));
    dispatch(setMfaError(null));
    analytics.capture('auth:mfa_submitted');
    try {
      const session = await auth.verifyMfa({
        challenge_token: challenge.challenge_token,
        otp_code,
      });
      dispatch(setMfaChallenge(null));
      dispatch(
        thunkSetSession(session, { method: pendingMfaMethod, via_mfa: true }),
      );
    } catch (e) {
      analytics.capture('auth:mfa_failed');
      dispatch(
        setMfaError(
          e instanceof Error ? e.message : 'Código inválido o expirado.',
        ),
      );
    } finally {
      dispatch(setLoading({ isLoading: false }));
    }
  };

export const thunkCancelMfa = (): AppThunk => (dispatch) => {
  dispatch(setMfaChallenge(null));
  dispatch(setMfaError(null));
};

export const thunkDoLoginToken =
  (tokenId: string): AppThunk<Promise<void>> =>
  async (dispatch, _getState, { auth }) => {
    if (!auth.tokenLogin) return;
    dispatch(setLoading({ isLoading: true }));
    analytics.capture('auth:login_submitted', { method: 'token' });
    try {
      const session = await auth.tokenLogin(tokenId);
      dispatch(thunkSetSession(session, { method: 'token', via_mfa: false }));
    } catch {
      analytics.capture('auth:login_failed', { method: 'token', reason: 'rejected' });
      dispatch(setUser({ user: undefined }));
    } finally {
      dispatch(setLoading({ isLoading: false }));
    }
  };

/** Opens the backend's Google OAuth entry point in the current tab. */
export const thunkGoogleLogin =
  (): AppThunk =>
  (_dispatch, _getState, { auth }) => {
    if (!auth.google) return;
    analytics.capture('auth:login_submitted', { method: 'google' });
    window.open(auth.google.buildLoginUrl(), '_self');
  };

/**
 * Handles the `?state=` payload the backend appends when Google redirects back
 * to the login page. Returns the outcome so the page can show feedback.
 */
export const thunkHandleOAuthCallback =
  (stateParam: string): AppThunk<OAuthCallbackResult> =>
  (dispatch, _getState, { auth }) => {
    if (!auth.google) return { kind: 'ignored' };
    const result = auth.google.parseCallback(stateParam);
    if (result.kind === 'mfa') {
      // Same OTP form and endpoint as the password flow.
      pendingMfaMethod = 'google';
      analytics.capture('auth:mfa_started', { method: 'google' });
      dispatch(setMfaChallenge(result.challenge));
    } else if (result.kind === 'success') {
      dispatch(
        thunkSetSession(result.session, { method: 'google', via_mfa: false }),
      );
    } else if (result.kind === 'forbidden') {
      analytics.capture('auth:login_failed', { method: 'google', reason: 'forbidden' });
    }
    return result;
  };

// Clears the local session synchronously so the caller can navigate to the
// login screen right away, then revokes the session on the backend
// best-effort in the background.
export const thunkDoLogout =
  (): AppThunk =>
  (dispatch, _getState, { auth }) => {
    const token = auth.tokens.get();
    analytics.capture('auth:session_ended', { reason: 'user' });
    auth.tokens.clear();
    dispatch(logout());
    void auth.logout(token);
  };
